(() => {
  const gate = document.querySelector('[data-entry-gate]');
  const enter = document.querySelector('[data-enter-store]');
  const home = document.querySelector('[data-home]');
  const liveEye = document.querySelector('[data-privateparts-entry-eye]');
  if (!gate || !enter || !home || !liveEye) return;

  const ACQUIRE_MS = 180;
  const STARE_HOLD_MS = 260;
  const DITHER_DELAY_MS = 125;
  const DITHER_MS = 700;
  const EXIT_TOTAL_MS = 1120;
  let running = false;

  const easeOutCubic = value => 1 - Math.pow(1 - value, 3);
  const lerp = (a, b, t) => a + (b - a) * t;

  const parseTranslate = value => {
    const match = String(value || '').match(/translate\(\s*([-+]?\d*\.?\d+)\s+([-+]?\d*\.?\d+)\s*\)/i);
    return match ? { x: Number(match[1]) || 0, y: Number(match[2]) || 0 } : { x: 0, y: 0 };
  };

  const parseScale = value => {
    const match = String(value || '').match(/scale\(\s*([-+]?\d*\.?\d+)/i);
    return match ? Number(match[1]) || 1 : 1;
  };

  const animateAttribute = (element, render, duration) => new Promise(resolve => {
    const start = performance.now();
    const frame = now => {
      const progress = Math.min(1, (now - start) / duration);
      render(easeOutCubic(progress));
      if (progress < 1) requestAnimationFrame(frame);
      else resolve();
    };
    requestAnimationFrame(frame);
  });

  const makeStareSnapshot = () => {
    const snapshot = liveEye.cloneNode(true);
    snapshot.removeAttribute('data-privateparts-entry-eye');
    snapshot.classList.add('privateparts-entry-eye--exit-snapshot');
    snapshot.setAttribute('aria-hidden', 'true');
    snapshot.style.setProperty('opacity', '1', 'important');
    snapshot.style.setProperty('transform', 'none', 'important');
    snapshot.style.setProperty('transition', 'none', 'important');
    snapshot.style.pointerEvents = 'none';
    gate.appendChild(snapshot);

    liveEye.style.visibility = 'hidden';

    const rig = snapshot.querySelector('[data-privateparts-eye-rig]');
    const pupilMove = snapshot.querySelector('[data-pp-pupil-move]');
    const pupilScale = snapshot.querySelector('[data-pp-pupil-scale]');
    const blur = snapshot.querySelector('[data-pp-motion-blur]');
    if (blur) blur.replaceChildren();

    const fromTranslate = parseTranslate(pupilMove?.getAttribute('transform'));
    const fromScale = parseScale(pupilScale?.getAttribute('transform'));
    const eyeCenterX = 71.75;
    const eyeCenterY = 31.89;

    if (rig) {
      const currentTransform = getComputedStyle(rig).transform === 'none'
        ? (rig.style.transform || 'none')
        : rig.style.transform || getComputedStyle(rig).transform;
      rig.animate([
        { transform: currentTransform },
        { transform: 'perspective(900px) rotateZ(0deg) rotateX(0deg) rotateY(0deg) translateZ(0px) scale(1,1)' }
      ], {
        duration: 260,
        easing: 'cubic-bezier(.16,.82,.2,1)',
        fill: 'forwards'
      });
    }

    const pupilAcquire = animateAttribute(pupilMove, t => {
      const x = lerp(fromTranslate.x, 0, t);
      const y = lerp(fromTranslate.y, 0, t);
      pupilMove?.setAttribute('transform', `translate(${x.toFixed(3)} ${y.toFixed(3)})`);
    }, ACQUIRE_MS);

    animateAttribute(pupilScale, t => {
      const scale = lerp(fromScale, Math.max(1.16, fromScale), Math.min(1, t * .9));
      pupilScale?.setAttribute('transform', `translate(${eyeCenterX} ${eyeCenterY}) scale(${scale.toFixed(5)}) translate(${-eyeCenterX} ${-eyeCenterY})`);
    }, 540);

    return { snapshot, pupilAcquire };
  };

  // Load the dot sequence once while the entry eye is idle. Each frame is
  // already rasterized into the sprite sheet, so entry needs no pixel reads.
  const sprite = new Image();
  const spriteReady = new Promise(resolve => {
    sprite.onload = () => resolve(true);
    sprite.onerror = () => resolve(false);
  });
  sprite.src = gate.dataset.entryDitherSprite || '';

  const startEyeDither = (snapshot, ready) => {
    if (!ready || !document.body.contains(gate)) {
      snapshot.style.opacity = '0';
      return;
    }
    const svg = snapshot.querySelector('[data-privateparts-eye-logo]');
    if (!svg) return;
    const bounds = svg.getBoundingClientRect();
    const scale = bounds.width / 130;
    const cssWidth = 246 * scale;
    const cssHeight = 257 * scale;
    const frame = document.createElement('div');
    frame.className = 'entry-logo-dither';
    frame.setAttribute('aria-hidden', 'true');
    frame.style.width = `${cssWidth}px`;
    frame.style.height = `${cssHeight}px`;
    frame.style.left = `${bounds.left - 58 * scale}px`;
    frame.style.top = `${bounds.top - 58 * scale}px`;
    frame.style.backgroundImage = `url("${sprite.src}")`;
    frame.style.backgroundSize = '700% 400%';
    frame.style.backgroundRepeat = 'no-repeat';
    frame.style.backgroundPosition = '0 0';
    frame.style.animation = `privateparts-entry-sprite ${DITHER_MS}ms steps(1,end) both`;
    gate.appendChild(frame);
    gate.classList.add('has-logo-dither');
    snapshot.style.setProperty('opacity', '0', 'important');
    frame.addEventListener('animationend', () => {
      home.classList.add('is-collections-revealed');
      frame.remove();
    }, { once:true });
  };

  enter.addEventListener('click', event => {
    if (running) return;
    running = true;
    event.preventDefault();
    event.stopImmediatePropagation();

    enter.disabled = true;
    enter.style.transition = 'opacity .14s ease';
    enter.style.opacity = '0';
    enter.style.pointerEvents = 'none';

    const { snapshot, pupilAcquire } = makeStareSnapshot();

    gate.classList.add('is-entering');
    pupilAcquire.then(() => {
      const held = new Promise(resolve => window.setTimeout(resolve, STARE_HOLD_MS));
      Promise.all([spriteReady, held]).then(([ready]) => {
        if (!document.body.contains(gate)) return;
        gate.classList.add('is-exiting');
        window.setTimeout(() => startEyeDither(snapshot, ready), DITHER_DELAY_MS);
        window.setTimeout(() => {
          home.classList.add('is-collections-revealed');
          document.documentElement.classList.remove('cobalt-entry-active');
          gate.remove();
        }, EXIT_TOTAL_MS);
      });
    });
  }, true);
})();


